import { useState } from "react";

import { FeatureFlag } from "@vega-api/extended/auth/useFeatureFlag";
import { type NotificationResponse } from "@vega-api/generated/types/notificationResponse";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { NotificationCenter } from "@vega-core/components/NotificationCenter/NotificationCenter";
import { m } from "@vega-core/paraglide/messages";
import { LPPageContainerWithNavigation } from "@vega-modules/components/PageContainerWithNavigation/PageContainerWithNavigation";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CommonPaths } from "@vega-modules/paths";
import { CoInvestNotificationsList } from "@vega-modules/scenes/CoInvestments/components/CoInvestNotificationsList/CoInvestNotificationsList";
import { COINVEST_NOTIFICATION_PUBLISHER } from "@vega-modules/scenes/CoInvestments/constants/notificationConstants";
import { useNotificationCenterData } from "@vega-modules/scenes/Notifications/useNotificationCenterData";

import { NotificationsDealFilterDropdown } from "../components/NotificationsDealFilterDropdown/NotificationsDealFilterDropdown";

export const CoInvestNotificationsPage = () => {
  useFeatureFlagGuard(FeatureFlag.COINVEST_NOTIFICATIONS, CommonPaths.Home);

  const [selectedOpportunityId, setSelectedOpportunityId] = useState<string>();

  const notificationCenterProps = useNotificationCenterData(
    {
      publisher: COINVEST_NOTIFICATION_PUBLISHER,
      ...(selectedOpportunityId
        ? { metadata: { dealOpportunityId: selectedOpportunityId } }
        : {}),
    },
    { readVisibleItemsOnly: true },
  );

  const hasUnread = notificationCenterProps.items.some((item) => !item.readAt);

  return (
    <LPPageContainerWithNavigation showNavbar={false}>
      <div className="p-4 lg:p-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <h4 className="text-text/primary heading-4">
            {m.coInvestments_notifications_title()}
          </h4>
          <div className="flex items-center gap-4">
            {hasUnread ? (
              <ThemedButton
                dataTestId="coinvest-notifications-mark-all-read"
                onClick={() => notificationCenterProps.onMarkAllRead()}
                variant="text"
              >
                {m.notificationCenter_markAllRead()}
              </ThemedButton>
            ) : null}
            <NotificationsDealFilterDropdown
              onChange={setSelectedOpportunityId}
              value={selectedOpportunityId}
            />
          </div>
        </div>

        <div className="mt-6">
          <NotificationCenter<NotificationResponse>
            {...notificationCenterProps}
          >
            <CoInvestNotificationsList
              emptyDescription={m.coInvestments_notifications_empty_subtitle()}
              emptyTitle={m.coInvestments_notifications_empty_title()}
            />
          </NotificationCenter>
        </div>
      </div>
    </LPPageContainerWithNavigation>
  );
};
